// Small, theme-aware chart building blocks for report pages (Campaign Detailed
// Report, Agent Performance Report, …). Recharts + shadcn CSS tokens, same look
// as the call-stats charts (features/cti/components/call-stats/charts.tsx).
//
// Charts only ever receive aggregated numbers (a summary object or a per-agent
// list) — never one page of a server-paged table, which would mislead.
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** Semantic series colours — mid tones that read on light and dark cards. */
export const REPORT_COLORS = {
  good: '#16a34a',
  crit: '#dc2626',
  warn: '#d97706',
  call: '#2563eb',
  info: '#0891b2',
  violet: '#7c3aed',
  off: '#94a3b8',
} as const;

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };
const TOOLTIP_STYLE = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 10,
  fontSize: 12,
  color: 'hsl(var(--foreground))',
};
const TOOLTIP_LABEL_STYLE = { color: 'hsl(var(--foreground))', fontWeight: 600, marginBottom: 4 };

export function ChartEmpty({ label, height }: { label: string; height: number }) {
  return (
    <div
      className="flex items-center justify-center text-[12px] text-muted-foreground"
      style={{ height }}
    >
      {label}
    </div>
  );
}

/** Card frame shared by every report chart. */
export function ReportChartCard({
  title,
  subtitle,
  actions,
  children,
  className = '',
}: {
  title: string;
  subtitle?: string;
  /** Controls in the card header (e.g. a "Top N" selector). */
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex h-full min-w-0 flex-col rounded-2xl border border-border/60 bg-card shadow-sm ${className}`}>
      <div className="flex items-center justify-between gap-3 border-b border-border/40 px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-[13px] font-semibold text-foreground">{title}</h3>
          {subtitle ? <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
      <div className="min-w-0 flex-1 p-3">{children}</div>
    </div>
  );
}

/** "Top 8 / 15 / 25 / All" picker for bar charts whose height grows with the rows. 0 = all. */
export const TOP_N_OPTIONS = [8, 15, 25, 0];
export function TopNSelect({
  value,
  onChange,
  label = 'Show',
}: {
  value: number;
  onChange: (n: number) => void;
  label?: string;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="input-embossed h-7 rounded-md px-2 text-[11px]"
      >
        {TOP_N_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n ? `Top ${n}` : 'All'}
          </option>
        ))}
      </select>
    </label>
  );
}

export interface DonutSlice {
  name: string;
  value: number;
  color: string;
}

/** Donut with the total in the middle and a value / % legend beside it. */
export function DonutChart({
  data,
  centerLabel = 'Total',
  height = 220,
  emptyLabel = 'No data yet',
}: {
  data: DonutSlice[];
  centerLabel?: string;
  height?: number;
  emptyLabel?: string;
}) {
  const slices = useMemo(() => data.filter((d) => d.value > 0), [data]);
  const total = useMemo(() => slices.reduce((s, d) => s + d.value, 0), [slices]);
  if (!total) return <ChartEmpty label={emptyLabel} height={height} />;

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="relative" style={{ width: height, height, maxWidth: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={slices.length > 1 ? 2 : 0}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((s) => (
                <Cell key={s.name} fill={s.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(v) => [Number(v).toLocaleString('en-IN'), '']}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold tabular-nums text-foreground">{total.toLocaleString('en-IN')}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{centerLabel}</span>
        </div>
      </div>
      <ul className="min-w-[140px] flex-1 space-y-1.5">
        {data.map((d) => (
          <li key={d.name} className="flex items-center gap-2 text-[12px]">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: d.color }} />
            <span className="flex-1 truncate text-foreground">{d.name}</span>
            <span className="tabular-nums font-semibold text-foreground">{d.value.toLocaleString('en-IN')}</span>
            <span className="w-12 text-right tabular-nums text-muted-foreground">
              {total ? `${((d.value / total) * 100).toFixed(1)}%` : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface BarSeries {
  key: string;
  label: string;
  color: string;
  /** Row field holding a per-bar colour (single-series charts like a funnel). */
  colorKey?: string;
}

/**
 * Horizontal bars, one row per category (agent, end reason, …). Several series
 * render side by side (grouped), or `stacked` when they are parts of one whole.
 */
export type BarRow = Record<string, string | number>;

/**
 * Vertical columns over an ordered axis (hour of day, date, …). `stacked` when the
 * series are parts of one whole (e.g. connected + failed = all calls that day).
 */
export function ColumnChart({
  data,
  categoryKey,
  series,
  stacked = false,
  height = 240,
  emptyLabel = 'No data yet',
}: {
  data: BarRow[];
  categoryKey: string;
  series: BarSeries[];
  stacked?: boolean;
  height?: number;
  emptyLabel?: string;
}) {
  const hasData = data.some((row) => series.some((s) => Number(row[s.key] || 0) > 0));
  if (!hasData) return <ChartEmpty label={emptyLabel} height={height} />;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 4, right: 8, left: -12, bottom: 0 }} barCategoryGap="18%">
        <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="hsl(var(--border))" />
        <XAxis
          dataKey={categoryKey}
          axisLine={false}
          tickLine={false}
          tickMargin={8}
          tick={AXIS_TICK}
          interval="preserveStartEnd"
          minTickGap={8}
        />
        <YAxis axisLine={false} tickLine={false} width={36} allowDecimals={false} tick={AXIS_TICK} />
        <Tooltip
          cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={(v, name) => [Number(v).toLocaleString('en-IN'), name]}
        />
        {series.length > 1 ? (
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
        ) : null}
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={s.color}
            stackId={stacked ? 'stack' : undefined}
            radius={stacked ? (i === series.length - 1 ? [4, 4, 0, 0] : 0) : [4, 4, 0, 0]}
            maxBarSize={28}
            isAnimationActive={false}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function HBarChart({
  data,
  categoryKey,
  series,
  stacked = false,
  valueFormatter = (v: number) => v.toLocaleString('en-IN'),
  onBarClick,
  emptyLabel = 'No data yet',
  rowHeight = 30,
}: {
  data: BarRow[];
  categoryKey: string;
  series: BarSeries[];
  stacked?: boolean;
  valueFormatter?: (v: number) => string;
  onBarClick?: (row: BarRow) => void;
  emptyLabel?: string;
  rowHeight?: number;
}) {
  const rowsPerCategory = stacked ? 1 : series.length;
  const height = Math.max(160, data.length * rowHeight * Math.max(1, rowsPerCategory * 0.7) + 48);
  if (!data.length) return <ChartEmpty label={emptyLabel} height={180} />;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 4, right: 16, left: 4, bottom: 0 }}
        barCategoryGap="22%"
      >
        <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="hsl(var(--border))" />
        <XAxis
          type="number"
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          tick={AXIS_TICK}
          tickFormatter={(v) => valueFormatter(Number(v))}
        />
        <YAxis
          type="category"
          dataKey={categoryKey}
          axisLine={false}
          tickLine={false}
          width={130}
          tick={AXIS_TICK}
          tickFormatter={(v) => (String(v).length > 18 ? `${String(v).slice(0, 17)}…` : String(v))}
        />
        <Tooltip
          cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
          contentStyle={TOOLTIP_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={(v, name) => [valueFormatter(Number(v)), name]}
        />
        {series.length > 1 ? (
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
        ) : null}
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.label}
            fill={s.color}
            stackId={stacked ? 'stack' : undefined}
            radius={stacked ? (i === series.length - 1 ? [0, 4, 4, 0] : 0) : [0, 4, 4, 0]}
            maxBarSize={18}
            isAnimationActive={false}
            cursor={onBarClick ? 'pointer' : undefined}
            onClick={onBarClick ? (entry: { payload?: BarRow }) => entry?.payload && onBarClick(entry.payload) : undefined}
          >
            {s.colorKey
              ? data.map((row, idx) => <Cell key={idx} fill={String(row[s.colorKey as string] || s.color)} />)
              : null}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
